import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { RosterEntryDto } from '../../types/community'
import { useRoster, type RosterFilter, type RosterSort } from '../../hooks/useRoster'
import { useToast } from '../../contexts/ToastContext'
import { createFriendRequest } from '../../lib/api/friends'
import { StatBlock } from '../ui/StatBlock'
import { Pill } from '../ui/Pill'
import { cn } from '../../lib/cn'
import { KudosModal } from '../safety/KudosModal'
import { usePerson } from '../../contexts/PersonContext'
import { Button } from '../ui/Button'

const SORT_OPTIONS: Array<{ value: RosterSort; label: string }> = [
  { value: 'online', label: 'Online first' },
  { value: 'rep', label: 'Highest kindness' },
  { value: 'played', label: 'Most played with us' },
  { value: 'new', label: 'Newest members' },
]

const FILTER_PILLS: Array<{ value: RosterFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'online', label: 'Online now' },
  { value: 'trusted', label: 'Trusted only' },
  { value: 'played', label: 'Played with us before' },
  { value: 'watched', label: 'Being watched' },
]

function kindnessColor(k: number): string {
  if (k >= 4.7) return 'text-moss'
  if (k < 4.2) return 'text-honey-fg'
  return 'text-ink-muted'
}

/** Standing pill tone: TRUSTED green, WATCHED amber, GOOD neutral. */
function standingTone(standing: string): 'moss' | 'honey' | 'neutral' {
  if (standing === 'Trusted') return 'moss'
  if (standing === 'Watched') return 'honey'
  return 'neutral'
}

interface CircleRosterProps {
  circleId: string
  /** Fallback stats before the roster query resolves (from CircleDetailDto). */
  fallbackStats?: { members: number; online: number; roomsOpen: number; kindness: number }
}

/**
 * The anonymous gaming roster (COTC_Circles R4). Handles-only rows, stats strip, search, sort,
 * filter pills, and "Show 24 more" paging. Every optional field is guarded (pitfall P2).
 */
export function CircleRoster({ circleId, fallbackStats }: CircleRosterProps) {
  const roster = useRoster(circleId)
  const navigate = useNavigate()
  const { flash } = useToast()
  const qc = useQueryClient()
  const [added, setAdded] = useState<Record<string, boolean>>({})
  const [requests, setRequests] = useState<Record<string, { pending?: boolean; error?: string }>>({})
  const inFlight = useRef(new Set<string>())
  const [kudos, setKudos] = useState<string | null>(null)
  const { activePerson } = usePerson()
  const generation = useRef(0)
  useEffect(() => {
    generation.current += 1
    inFlight.current.clear()
    setAdded({}); setRequests({}); setKudos(null)
    return () => { generation.current += 1 }
  }, [circleId, activePerson?.id])

  const stats = roster.stats ?? fallbackStats ?? { members: 0, online: 0, roomsOpen: 0, kindness: 0 }

  async function onAdd(entry: RosterEntryDto) {
    const handle = entry.handle || ''
    const state = added[handle] ? 'requested' : entry.addState
    if (state === 'friends') {
      flash('Already friends — find them under My friends.')
      return
    }
    if (state === 'requested') {
      flash('Already requested — awaiting a decision.')
      return
    }
    if (!handle || inFlight.current.has(handle)) return
    inFlight.current.add(handle)
    const requestGeneration = generation.current
    setRequests((old) => ({ ...old, [handle]: { pending: true } }))
    try {
      await createFriendRequest({ targetHandle: handle })
      if (requestGeneration !== generation.current) return
      setAdded((old) => ({ ...old, [handle]: true }))
      setRequests((old) => ({ ...old, [handle]: {} }))
      flash('Sent. The required family approvals still apply.')
      for (const key of ['roster', 'directory', 'friends', 'approvals', 'me']) void qc.invalidateQueries({ queryKey: ['community', key] })
    } catch (error) {
      if (requestGeneration !== generation.current) return
      setRequests((old) => ({ ...old, [handle]: { error: error instanceof Error ? error.message : 'That request did not go through. Try again.' } }))
    } finally { if (requestGeneration === generation.current) inFlight.current.delete(handle) }
  }

  function onReport(entry: RosterEntryDto) {
    const handle = entry.handle || ''
    navigate(`/report?circleId=${encodeURIComponent(circleId)}&handle=${encodeURIComponent(handle)}`)
  }

  function addLabel(entry: RosterEntryDto): string {
    const handle = entry.handle || ''
    const state = added[handle] ? 'requested' : entry.addState
    if (state === 'friends') return 'Friends ✓'
    if (state === 'requested') return 'Requested'
    return 'Add to friends'
  }

  return (
    <div>
      {/* Stats strip */}
      <div className="mb-[18px] flex flex-wrap gap-[22px] rounded-card border border-line-soft bg-paper-panel px-5 py-4">
        <StatBlock label="members" value={stats.members} />
        <StatBlock label="online now" value={stats.online} positive />
        <StatBlock label="rooms open" value={stats.roomsOpen} />
        <StatBlock label="circle kindness" value={stats.kindness.toFixed(1)} positive />
      </div>

      {/* Search + sort */}
      <div className="mb-3 flex flex-wrap items-center gap-[10px]">
        <input
          value={roster.query}
          onChange={(e) => roster.setQuery(e.target.value)}
          placeholder="Search handles…"
          aria-label="Search handles"
          className="w-full min-w-0 flex-[1_1_220px] rounded-input border border-line bg-paper-surface px-[14px] py-[11px] text-[13.5px] text-ink outline-none focus:border-terra"
        />
        <select
          value={roster.sort}
          onChange={(e) => roster.setSort(e.target.value as RosterSort)}
          aria-label="Sort roster"
          className="rounded-input border border-line bg-paper-surface px-3 py-[10px] text-[13px] text-ink outline-none focus:border-terra"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Filter pills */}
      <div className="mb-4 flex flex-wrap gap-[7px]">
        {FILTER_PILLS.map((f) => {
          const active = roster.filter === f.value
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={active}
              onClick={() => roster.setFilter(f.value)}
              className={cn(
                'rounded-pill border px-[13px] py-2 text-[12px] font-medium transition-colors',
                active
                  ? 'border-ink bg-ink text-paper-panel'
                  : 'border-line bg-transparent text-ink-muted hover:border-terra hover:text-terra-deep',
              )}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {/* Roster rows */}
      <div className="overflow-hidden rounded-card border border-line-soft">
        {roster.isError ? (
          <div role="alert" className="space-y-3 p-5 text-sm text-ink-muted">
            <p>Players could not be loaded. Try again.</p>
            <Button variant="outline" onClick={() => void roster.refetch()}>Retry players</Button>
          </div>
        ) : roster.entries.length === 0 ? (
          <div className="px-5 py-8 text-center text-[13.5px] text-ink-faint">
            {roster.isLoading ? 'Loading players…' : 'No players match that yet.'}
          </div>
        ) : (
          roster.entries.map((m, i) => {
            const handle = m.handle || ''
            const glyph = (m.glyph || handle.slice(0, 2) || '··').toUpperCase()
            const kindness = typeof m.kindness === 'number' ? m.kindness : 0
            const standing = m.standing || 'Good'
            return (
              <div
                key={handle || i}
                className="flex flex-wrap items-center gap-3 border-b border-line-row px-[18px] py-[13px] last:border-b-0"
              >
                <div className="flex min-w-0 flex-[1_1_240px] items-center gap-[11px]">
                  <span className="relative h-[30px] w-[30px] flex-none">
                    <span className="flex h-full w-full items-center justify-center rounded-[7px] bg-tint2 font-mono text-[11px] text-[#332a20]">
                      {glyph}
                    </span>
                    <span
                      className={cn(
                        'absolute -bottom-[2px] -right-[2px] h-[9px] w-[9px] rounded-full border-2 border-paper-surface',
                        m.online ? 'bg-moss' : 'bg-[#c9bda9]',
                      )}
                    />
                  </span>
                  <div className="min-w-0">
                    <div className="break-all font-mono text-[13.5px] font-medium text-ink">{handle}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-[9px]">
                      <span className={cn('font-mono text-[11.5px]', kindnessColor(kindness))}>
                        Kindness {kindness.toFixed(1)}
                      </span>
                      <span className="font-mono text-[11.5px] text-ink-faint">
                        {m.sessions == null ? '—' : `${m.sessions} sessions`}
                      </span>
                      <Pill tone={standingTone(standing)} className="text-[10px] uppercase tracking-label">
                        {standing.toUpperCase()}
                      </Pill>
                      <span className="text-[11.5px] text-ink-faint">{m.doingText || ''}</span>
                    </div>
                  </div>
                </div>
                <div className="ml-auto flex flex-none gap-[6px]">
                  <button
                    type="button"
                    title="Give kudos"
                    onClick={() => setKudos(handle)}
                    className={cn(
                      'whitespace-nowrap rounded-btn border px-[11px] py-2 text-[11.5px] font-medium',
                      m.kudosGivenByMe
                        ? 'border-moss-border bg-moss-bg text-moss'
                        : 'border-line bg-paper-surface text-ink-muted',
                    )}
                  >
                    {'♡'} {m.kudosCount ?? 0}
                  </button>
                  <button
                    type="button"
                    disabled={requests[handle]?.pending}
                    onClick={() => onAdd(m)}
                    className="whitespace-nowrap rounded-btn border border-line bg-paper-surface px-3 py-2 text-[11.5px] font-medium text-ink"
                  >
                    {requests[handle]?.pending ? 'Sending request…' : addLabel(m)}
                  </button>
                  <button
                    type="button"
                    onClick={() => onReport(m)}
                    className="whitespace-nowrap rounded-btn border border-honey-border bg-transparent px-[11px] py-2 text-[11.5px] font-medium text-honey-fg"
                  >
                    Report
                  </button>
                </div>
                {requests[handle]?.error && <p role="alert" className="w-full break-words text-[12px] text-honey-fg">{requests[handle].error}</p>}
              </div>
            )
          })
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-paper-row px-[18px] py-[14px]">
          <div className="font-mono text-[12px] text-ink-faint">{roster.showingText}</div>
          <button
            type="button"
            onClick={roster.hasMore ? roster.showMore : undefined}
            disabled={!roster.hasMore}
            className={cn(
              'rounded-btn border border-line bg-paper-surface px-4 py-[9px] text-[12.5px] font-medium',
              roster.hasMore ? 'text-ink-muted hover:border-terra hover:text-terra-deep' : 'text-ink-faint',
            )}
          >
            {roster.moreLabel}
          </button>
        </div>
      </div>

      <p className="mt-3 max-w-[64ch] text-[12.5px] leading-relaxed text-ink-faint">
        This roster uses player handles. Shared-circle discovery also shows first names and ages.
        Friendship requests follow each profile's approval settings. Authorised moderators can
        resolve a handle to the responsible family.
      </p>
      {kudos && <KudosModal open targetName={kudos} targetHandle={kudos} onClose={() => setKudos(null)} />}
    </div>
  )
}
